import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCrudOperations } from '../components/Hooks/useCrudOperations';
import { API_URL } from '../utils/api';
import LeadmeLogo from '../assets/images/leadme-site-logo.png';

function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { addItem } = useCrudOperations(`${API_URL}/auth/register`, null, null);

  const validatePassword = (password) => {
    // Tillåt alla tecken (inkl. å ä ö/Unicode). Krav: minst 8 tecken, en bokstav + en siffra.
    const passwordRegex = /^(?=.*\p{L})(?=.*\d).{8,}$/u;
    return passwordRegex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validatePassword(password)) {
      setErrorMessage('Lösenordet måste vara minst 8 tecken långt och innehålla både bokstäver och siffror.');
      setLoading(false);
      return;
    }

    try {
      await addItem({
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        phone: phone || '',
        password: password || '',
        role: 'kund',
        procurementStatus: 'Ny registrering'
      });
      navigate('/login');
    } catch (error) {
      setErrorMessage('Kunde inte registrera användare');
      console.error('Error:', error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-leadme-pink-light/20">
      <div className="w-full max-w-md">
        <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl p-10 space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img 
              src={LeadmeLogo}
              alt="LeadMe Logotyp"
              className="h-8 transition-transform duration-200"
            />
          </div>

          <p>
            Fyll i dina uppgifter och registrera dig.
          </p>

          {errorMessage && (
            <div className="p-4 rounded-xl bg-leadme-red-light/20 border border-leadme-red-light">
              <p className="text-sm text-leadme-red text-center">
                {errorMessage}
              </p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Förnamn
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Efternamn
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-post
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                placeholder="john.doe@exempel.se"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefon <span className="text-gray-400 font-normal">(valfritt)</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                placeholder="070-123 45 67"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lösenord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                placeholder="••••••••"
              />
              <p className="mt-2 text-xs text-gray-500">
                Minst 8 tecken, innehåller både bokstäver och siffror
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-leadme-pink text-white text-lg font-semibold rounded-xl
              transition-all duration-200
              hover:bg-leadme-pink-dark active:bg-leadme-pink-dark
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
            >
              {loading ? (
                <svg className="animate-spin h-6 w-6 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : 'Registrera'}
            </button>
          </form>

          <div>
            <p className="text-center text-sm text-gray-600">
              Har du redan ett konto?{' '}
              <Link 
                to="/login" 
                className="font-semibold text-primary"
              >
                Logga in här →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
